import { Textarea, createStyles, Box, Button, Tooltip } from '@mantine/core';
import { useEffect, useState } from 'react';

const useStyles = createStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        position: 'relative',
    },
    textarea: {
        backgroundColor: 'rgba(32, 32, 32, 1)',
        border: 'none',
        borderRadius: '6px',
        '&:focus': {
            backgroundColor: 'rgba(32, 32, 32, 1)',
        },
        '&:focus-within': {
            borderColor: 'rgba(245, 60, 2, 1)',
        },
    },
    bottomBox: {
        backgroundColor: 'rgba(32, 32, 32, 1)',
        border: 'none',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
        borderBottomLeftRadius: '6px',
        borderBottomRightRadius: '6px',
        // height: '40px',
    },
}));

interface PromptFieldProps {
    value: string;
    onChangeEnd: (value: string) => void;
    onClick: () => void;
    placeholder?: string;
    children: any
    loading: boolean
    disabled?: boolean
    credits: number
}

export const PromptField = ({ children, disabled, credits, value, onChangeEnd, placeholder = "Enter your prompt...", loading, onClick }: PromptFieldProps) => {
    const { classes } = useStyles();
    const [inputValue, setInputValue] = useState(value);

    const handleChange = (newValue: string) => {
        setInputValue(newValue);
    };

    const handleBlur = () => {
        onChangeEnd(inputValue);
    };

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    return (
        <div className={classes.container}>
            <Textarea
                value={inputValue}
                onChange={(e) => handleChange(e.currentTarget.value)}
                onBlur={handleBlur}
                className={classes.textarea}
                disabled={disabled}
                minRows={3}
                placeholder={placeholder}
                styles={{
                    input: {
                        fontSize: '16px',
                        border: 'none',
                        // borderRadius: '6px',
                        borderTopLeftRadius: '6px',
                        borderTopRightRadius: '6px',
                        borderBottomLeftRadius: '0px',
                        borderBottomRightRadius: '0px',
                        background: 'rgba(32, 32, 32, 1)',
                        '&:focus': {
                            borderColor: 'rgba(245, 60, 2, 1)',
                        },
                    },
                }}
            />
            <Tooltip p={12} label={
                <span>Generation cost: <span style={{
                        color: '#f53c02',
                        fontWeight: 700
                    }}>{credits} credits</span>
                </span>
            } withArrow position="right" color='dark'>
                <Button
                    w={'48px'}
                    h={'48px'}
                    loading={loading}
                    disabled={loading}
                    onClick={onClick}
                    variant="filled"
                    data-intercom-target={
                        'GenerateButton'
                    }
                    style={{
                        padding: '0',
                        position: 'absolute',
                        right: '10px',
                        bottom: '10px'
                    }}
                    color="orange">
                    {
                        loading ? null : <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21.9169 16.13C21.922 16.3487 21.8553 16.563 21.7269 16.74C21.6003 16.9206 21.4174 17.0542 21.2069 17.12L19.4969 17.69C18.9702 17.865 18.4911 18.1593 18.0969 18.55C17.7033 18.9419 17.4085 19.4217 17.2369 19.95L16.6369 21.65C16.5744 21.8541 16.4483 22.0328 16.2769 22.16C16.0941 22.2842 15.878 22.3504 15.6569 22.35C15.4351 22.3595 15.2164 22.2949 15.0353 22.1663C14.8543 22.0378 14.7211 21.8526 14.6569 21.64L14.0869 19.93C13.9154 19.4017 13.6206 18.9219 13.2269 18.53C12.8296 18.1411 12.3516 17.8441 11.8269 17.66L10.1169 17.1C9.91451 17.0291 9.73723 16.9005 9.60695 16.73C9.47565 16.5495 9.40241 16.3332 9.39695 16.11C9.38747 15.8882 9.4521 15.6695 9.58065 15.4884C9.70921 15.3074 9.89437 15.1742 10.1069 15.11L11.8269 14.54C12.3604 14.3677 12.8452 14.0711 13.2416 13.6747C13.638 13.2783 13.9346 12.7935 14.1069 12.26L14.6769 10.57C14.7352 10.3621 14.8593 10.1786 15.0305 10.047C15.2018 9.91544 15.411 9.84282 15.6269 9.84004C15.8419 9.84004 16.0529 9.89904 16.2369 10.01C16.4189 10.135 16.5589 10.313 16.6369 10.52L17.2169 12.26C17.3893 12.7935 17.6859 13.2783 18.0823 13.6747C18.4787 14.0711 18.9635 14.3677 19.4969 14.54L21.1969 15.14C21.4038 15.2078 21.5828 15.3412 21.7069 15.52C21.8424 15.6946 21.9163 15.9091 21.9169 16.13ZM11.9179 9.77004C11.9164 9.96607 11.8573 10.1573 11.7479 10.32C11.6297 10.481 11.4661 10.6029 11.2779 10.67L10.0179 11.09C9.66495 11.212 9.34495 11.41 9.07795 11.67C8.81695 11.9362 8.61878 12.2574 8.49795 12.61L8.06795 13.85C8.00698 14.0414 7.88378 14.2068 7.71795 14.32C7.55355 14.4348 7.35844 14.4975 7.15795 14.5C6.95296 14.4966 6.75398 14.4303 6.58795 14.31C6.43067 14.1904 6.31235 14.0269 6.24795 13.84L5.83795 12.59C5.71764 12.2401 5.51931 11.922 5.25795 11.66C5.00184 11.3913 4.68191 11.1918 4.32795 11.08L3.07795 10.66C2.88399 10.599 2.71538 10.476 2.59795 10.31C2.51835 10.1854 2.4674 10.0446 2.44876 9.89787C2.43013 9.75113 2.44428 9.60209 2.49019 9.46149C2.5361 9.32088 2.61263 9.19221 2.71427 9.08475C2.81591 8.97729 2.94012 8.89371 3.07795 8.84004L4.32795 8.43004C4.68164 8.30654 5.00292 8.10482 5.26782 7.83992C5.53273 7.57501 5.73445 7.25374 5.85795 6.90004L6.26795 5.67004C6.32689 5.48571 6.43805 5.32244 6.58795 5.20004C6.74723 5.08002 6.93877 5.01037 7.13795 5.00004C7.33974 4.99462 7.53845 5.0504 7.70795 5.16004C7.87476 5.27265 8.00372 5.43297 8.07795 5.62004L8.49795 6.90004C8.62145 7.25374 8.82316 7.57501 9.08807 7.83992C9.35298 8.10482 9.67425 8.30654 10.0279 8.43004L11.2779 8.86004C11.4649 8.92426 11.6262 9.04698 11.7379 9.21004C11.8563 9.37264 11.9194 9.56892 11.9179 9.77004ZM17.7069 4.41004C17.6979 4.59237 17.6391 4.76874 17.5369 4.92004C17.4329 5.06264 17.2856 5.16786 17.1169 5.22004L16.4969 5.43004C16.3765 5.4716 16.2671 5.54002 16.177 5.63011C16.0869 5.7202 16.0185 5.8296 15.9769 5.95004L15.7569 6.58004C15.6966 6.73345 15.5965 6.86804 15.4669 6.97004C15.3195 7.08814 15.1358 7.15173 14.9469 7.15004C14.7741 7.13931 14.6062 7.08792 14.4569 7.00004C14.3074 6.8899 14.1957 6.73621 14.1369 6.56004L13.9269 5.94004C13.8908 5.81708 13.8217 5.70639 13.7269 5.62004C13.6388 5.52753 13.5287 5.45874 13.4069 5.42004L12.7869 5.22004C12.622 5.15571 12.477 5.04874 12.3669 4.91004C12.2632 4.76016 12.2074 4.58232 12.2069 4.40004C12.2128 4.21712 12.2718 4.03987 12.3769 3.89004C12.4847 3.75118 12.6307 3.64689 12.7969 3.59004L13.4069 3.39004C13.5301 3.34802 13.6427 3.27978 13.7369 3.19004C13.8267 3.09579 13.8949 2.98321 13.9369 2.86004L14.1469 2.24004C14.2069 2.08504 14.3019 1.94804 14.4269 1.84004C14.5704 1.73415 14.7396 1.66855 14.9169 1.65004C15.1057 1.64896 15.2903 1.70471 15.4469 1.81004C15.5879 1.91609 15.6983 2.05754 15.7669 2.22004L15.9769 2.86004C16.019 2.98321 16.0872 3.09579 16.1769 3.19004C16.27 3.27641 16.3786 3.34429 16.4969 3.39004L17.1269 3.60004C17.2872 3.66308 17.4284 3.76641 17.5369 3.90004C17.646 4.04783 17.7055 4.22636 17.7069 4.41004Z" fill="white" />
                        </svg>
                    }

                </Button>
            </Tooltip>
            <Box className={classes.bottomBox} >
                {children}
            </Box>
        </div>
    );
};
